import { Link } from 'react-router-dom';
import { ArrowRight, Check, Info, FileText, Building, DollarSign, HelpCircle } from 'lucide-react';
import Breadcrumbs from '../components/shared/Breadcrumbs';
import AffiliateBanner from '../components/shared/AffiliateBanner';
import CTABox from '../components/shared/CTABox';
import TableOfContents from '../components/shared/TableOfContents';
import StarRating from '../components/shared/StarRating';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const LLCUSAPage = () => {
  const tocItems = [
    { id: 'was-ist-llc', title: 'Was ist eine LLC?', level: 2 },
    { id: 'vorteile', title: 'Vorteile für Deutsche/Europäer', level: 2 },
    { id: 'llc-vs-corp', title: 'LLC vs. Corporation', level: 2 },
    { id: 'bundesstaaten', title: 'Die besten Bundesstaaten', level: 2 },
    { id: 'schritt-fuer-schritt', title: 'Schritt-für-Schritt Anleitung', level: 2 },
    { id: 'kosten', title: 'Kosten einer LLC-Gründung', level: 2 },
    { id: 'testsieger', title: 'Unser Testsieger', level: 2 },
    { id: 'faq', title: 'Häufig gestellte Fragen', level: 2 },
  ];

  const stateComparison = [
    { state: 'Wyoming', filing: '$100', annual: '$60', tax: 'Keine', privacy: 'Sehr hoch', recommendation: true },
    { state: 'Delaware', filing: '$90', annual: '$300+', tax: 'Franchise Tax', privacy: 'Hoch', recommendation: false },
    { state: 'New Mexico', filing: '$50', annual: '$0', tax: 'Keine', privacy: 'Maximum', recommendation: false },
    { state: 'Nevada', filing: '$425', annual: '$350', tax: 'Keine', privacy: 'Hoch', recommendation: false },
    { state: 'Florida', filing: '$125', annual: '$138.75', tax: 'Keine', privacy: 'Mittel', recommendation: false },
  ];

  const costBreakdown = [
    { item: 'Staatsgebühren (Wyoming)', cost: '$100', frequency: 'Einmalig' },
    { item: 'Registered Agent', cost: '$125/Jahr', frequency: 'Jährlich (1. Jahr oft kostenlos)' },
    { item: 'EIN Service', cost: '$0-$79', frequency: 'Einmalig' },
    { item: 'Operating Agreement', cost: '$0 (inklusive)', frequency: 'Einmalig' },
    { item: 'Annual Report (Wyoming)', cost: '$60', frequency: 'Jährlich' },
  ];

  const faqs = [
    {
      question: 'Kann ich als Deutscher eine LLC gründen?',
      answer: 'Ja, absolut. Das US-Recht erlaubt es Ausländern ohne Einschränkungen, eine LLC zu gründen. Sie benötigen weder ein Visum noch einen Wohnsitz in den USA. Sie brauchen lediglich einen Registered Agent mit Adresse in Ihrem gewählten Bundesstaat, der als Ihr offizieller Ansprechpartner fungiert.'
    },
    {
      question: 'Brauche ich ein US-Visum für eine LLC?',
      answer: 'Nein, Sie benötigen kein Visum, um eine LLC zu besitzen oder zu betreiben. Die LLC-Gründung ist eine reine Geschäftstransaktion, die remote durchgeführt werden kann. Wenn Sie jedoch in die USA reisen möchten, um dort physisch zu arbeiten, benötigen Sie das entsprechende Arbeitsvisum.'
    },
    {
      question: 'Wie wird eine LLC in Deutschland besteuert?',
      answer: 'Eine LLC wird in Deutschland grundsätzlich wie eine Personengesellschaft behandelt, sofern Sie der einzige Gesellschafter sind (Single-Member LLC). Die Gewinne werden in Ihrer persönlichen deutschen Steuererklärung angegeben. Bei einer Multi-Member LLC kann es komplexer werden. Wir empfehlen dringend, einen auf internationales Steuerrecht spezialisierten Steuerberater zu konsultieren.'
    },
    {
      question: 'Was ist ein Registered Agent?',
      answer: 'Ein Registered Agent ist eine Person oder ein Unternehmen mit physischer Adresse in dem Bundesstaat, in dem Ihre LLC registriert ist. Der Registered Agent empfängt offizielle Dokumente, rechtliche Benachrichtigungen und Steuerunterlagen für Ihre LLC. Jede LLC ist gesetzlich verpflichtet, einen Registered Agent zu haben.'
    },
    {
      question: 'Wie lange dauert die Gründung?',
      answer: 'Die eigentliche Online-Gründung dauert nur 10-15 Minuten. Die Bearbeitungszeit durch den Bundesstaat variiert: Wyoming bearbeitet Anträge oft innerhalb von 1-2 Werktagen, Delaware kann 2-3 Wochen dauern. Gegen Aufpreis bieten die meisten Staaten eine beschleunigte Bearbeitung an.'
    },
    {
      question: 'Welche laufenden Kosten hat eine LLC?',
      answer: 'Die laufenden Kosten bestehen hauptsächlich aus der Registered Agent-Gebühr (ca. $125/Jahr) und dem Annual Report/Franchise Tax Ihres Bundesstaates. In Wyoming sind das nur $60/Jahr, in Delaware deutlich mehr. Hinzu kommen eventuelle Buchhalter- oder Steuerberaterkosten.'
    },
    {
      question: 'Kann ich eine LLC alleine gründen?',
      answer: 'Ja, eine sogenannte Single-Member LLC ist möglich und sehr verbreitet. Sie können als alleiniger Inhaber alle Entscheidungen treffen. Es gibt keine Mindestanzahl an Gesellschaftern erforderlich.'
    },
    {
      question: 'Brauche ich einen US-Anwalt?',
      answer: 'Für die reine Gründung einer LLC benötigen Sie keinen Anwalt. Die Gründungsdienstleister wie Northwest Registered Agent übernehmen alle notwendigen Schritte. Für komplexere rechtliche Fragen oder größere Unternehmensstrukturen kann jedoch die Konsultation eines US-Anwalts sinnvoll sein.'
    },
    {
      question: 'Was passiert, wenn ich meine LLC nicht mehr brauche?',
      answer: 'Sie können Ihre LLC formal auflösen (dissolve). Dazu reichen Sie die entsprechenden Unterlagen bei Ihrem Bundesstaat ein. Vorher sollten Sie alle Verbindlichkeiten begleichen und Ihr EIN bei der IRS schließen. Die Auflösungsgebühren variieren je nach Staat.'
    },
    {
      question: 'Kann ich mit einer LLC ein US-Bankkonto eröffnen?',
      answer: 'Ja, das ist möglich, auch als Ausländer. Einige Banken wie Mercury oder Relay ermöglichen die Kontoeröffnung komplett online. Traditionelle Banken verlangen oft eine persönliche Erscheinung in einer Filiale. Ihr Registered Agent kann Ihnen dabei helfen, die richtige Bank zu finden.'
    },
  ];

  return (
    <div className="py-12" data-testid="llc-usa-page">
      <div className="container-wide">
        <Breadcrumbs items={[{ label: 'LLC Gründung USA', href: '/llc-usa-gruenden' }]} />
        
        <AffiliateBanner />

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article className="prose-reviewlex">
              {/* Hero */}
              <header className="mb-12">
                <h1 className="mb-6">LLC Gründung USA — Der komplette Leitfaden 2026</h1>
                <p className="text-xl text-slate-600 mb-6">
                  Alles was Sie über die Gründung einer US-amerikanischen Limited Liability Company 
                  wissen müssen — von der Wahl des Bundesstaates bis zur EIN-Nummer.
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span>Aktualisiert: April 2026</span>
                  <span>•</span>
                  <span>Lesezeit: 15 Min.</span>
                </div>
              </header>

              {/* Was ist eine LLC */}
              <section id="was-ist-llc" className="mb-16">
                <h2>Was ist eine LLC?</h2>
                <p>
                  Eine <strong>Limited Liability Company (LLC)</strong> ist eine flexible Unternehmensform in den USA, 
                  die Elemente einer Kapitalgesellschaft mit denen einer Personengesellschaft kombiniert. Sie wurde 
                  erstmals 1977 in Wyoming eingeführt und ist heute in allen 50 Bundesstaaten verfügbar.
                </p>
                <p>
                  Der Name "Limited Liability" bezieht sich auf den wichtigsten Vorteil: Die Haftung der Gesellschafter 
                  (Members) ist auf ihre Einlage beschränkt. Ihr persönliches Vermögen ist vom Geschäftsvermögen getrennt 
                  und somit im Falle von Geschäftsschulden oder Klagen geschützt.
                </p>
                <p>
                  Für internationale Unternehmer, insbesondere aus dem deutschsprachigen Raum, ist die LLC besonders 
                  attraktiv, da sie ohne US-Staatsbürgerschaft oder Wohnsitz gegründet werden kann und gleichzeitig 
                  Zugang zum weltgrößten Verbrauchermarkt bietet.
                </p>
              </section>

              {/* Vorteile */}
              <section id="vorteile" className="mb-16">
                <h2>Vorteile einer LLC für Deutsche/Europäer</h2>
                
                <div className="grid gap-6 mb-8">
                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Haftungsbeschränkung (Asset Protection)
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Ihr persönliches Vermögen ist von Geschäftsschulden und Klagen getrennt. 
                      Dies ist besonders wichtig in den klagefreudigen USA.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Steuerliche Flexibilität
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Standardmäßig wird eine LLC als "Pass-Through Entity" besteuert — Gewinne fließen 
                      direkt zu den Members. Alternativ kann die LLC wählen, als Corporation besteuert zu werden.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Einfache Verwaltung
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Im Gegensatz zu Corporations gibt es keine Pflicht für Vorstandssitzungen, 
                      Protokolle oder komplexe Unternehmensstrukturen.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Datenschutz und Anonymität
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      In Staaten wie Wyoming oder New Mexico können Sie eine LLC gründen, ohne dass 
                      Ihr Name in öffentlichen Registern erscheint.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      Keine Residenzpflicht
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Sie müssen nicht in den USA leben oder Staatsbürger sein. Ein Registered Agent 
                      vor Ort ist ausreichend.
                    </p>
                  </div>

                  <div className="card-flat p-6">
                    <h4 className="font-heading font-bold text-navy-900 mb-2 flex items-center gap-2">
                      <Check className="w-5 h-5 text-teal-600" />
                      US-Bankkonto & Zahlungsabwicklung
                    </h4>
                    <p className="text-slate-600 text-sm mb-0">
                      Mit einer LLC können Sie ein US-Geschäftskonto eröffnen und US-Zahlungsanbieter 
                      wie Stripe nutzen — oft mit besseren Konditionen als in Europa.
                    </p>
                  </div>
                </div>
              </section>

              {/* LLC vs Corporation */}
              <section id="llc-vs-corp" className="mb-16">
                <h2>LLC vs. Corporation (Inc.) — Vergleich</h2>
                <p>
                  Beide Rechtsformen bieten Haftungsbeschränkung, unterscheiden sich jedoch in 
                  Besteuerung, Verwaltungsaufwand und Eignung für verschiedene Geschäftsmodelle.
                </p>

                <div className="overflow-x-auto mb-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Merkmal</TableHead>
                        <TableHead>LLC</TableHead>
                        <TableHead>Corporation (Inc.)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Besteuerung</TableCell>
                        <TableCell>Pass-Through (oder wählbar)</TableCell>
                        <TableCell>Doppelbesteuerung (Corp + Dividenden)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Verwaltung</TableCell>
                        <TableCell>Minimal</TableCell>
                        <TableCell>Vorstand, Protokolle, Sitzungen</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Investoren/VC</TableCell>
                        <TableCell>Weniger bevorzugt</TableCell>
                        <TableCell>Standardform für VC-Finanzierung</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Aktienoptionen</TableCell>
                        <TableCell>Kompliziert</TableCell>
                        <TableCell>Standardmäßig möglich</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Für wen geeignet</TableCell>
                        <TableCell>Freelancer, E-Commerce, Dienstleister</TableCell>
                        <TableCell>Startups mit Investoren, größere Unternehmen</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="card-highlight p-6">
                  <p className="mb-0 text-sm">
                    <strong>Unsere Empfehlung:</strong> Für die meisten internationalen Unternehmer, 
                    Freelancer und Online-Businesses ist die LLC die bessere Wahl aufgrund der 
                    einfacheren Verwaltung und steuerlichen Flexibilität.
                  </p>
                </div>
              </section>

              {/* Bundesstaaten */}
              <section id="bundesstaaten" className="mb-16">
                <h2>Die besten Bundesstaaten für Ihre LLC</h2>
                <p>
                  Obwohl Sie Ihre LLC in jedem der 50 US-Bundesstaaten gründen können, sind einige 
                  für internationale Unternehmer besonders vorteilhaft. Hier unsere Analyse:
                </p>

                <h3 className="mt-8 mb-4">Wyoming — Unsere #1 Empfehlung</h3>
                <p>
                  Wyoming war der erste Staat, der die LLC-Rechtsform einführte, und bietet auch 
                  heute noch die besten Konditionen:
                </p>
                <ul>
                  <li>Keine Staatseinkommensteuer</li>
                  <li>Starker Datenschutz — Mitglieder müssen nicht öffentlich genannt werden</li>
                  <li>Niedrige Gebühren: $100 Gründung, $60/Jahr Annual Report</li>
                  <li>Starker Vermögensschutz durch fortschrittliche LLC-Gesetze</li>
                  <li>Schnelle Bearbeitung (oft 1-2 Werktage)</li>
                </ul>

                <h3 className="mt-8 mb-4">Delaware — Für Investoren-Finanzierung</h3>
                <p>
                  Delaware ist bekannt für sein unternehmensfreundliches Rechtssystem und den 
                  spezialisierten Court of Chancery. Die höheren Gebühren und die Franchise Tax 
                  machen es für kleine Unternehmen weniger attraktiv.
                </p>

                <h3 className="mt-8 mb-4">New Mexico — Maximum Privacy</h3>
                <p>
                  New Mexico bietet den stärksten Datenschutz: Keine Annual Reports, keine jährlichen 
                  Gebühren nach der Gründung, und Mitglieder müssen nicht offengelegt werden. Die 
                  Einschränkung: Weniger etabliert als Wyoming für Geschäftszwecke.
                </p>

                <div className="overflow-x-auto mt-8 mb-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Bundesstaat</TableHead>
                        <TableHead>Gründung</TableHead>
                        <TableHead>Jährlich</TableHead>
                        <TableHead>Staatssteuer</TableHead>
                        <TableHead>Datenschutz</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stateComparison.map((row, index) => (
                        <TableRow 
                          key={index}
                          className={row.recommendation ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''}
                        >
                          <TableCell className="font-medium">
                            {row.state}
                            {row.recommendation && (
                              <span className="ml-2 text-xs bg-teal-600 text-white px-2 py-0.5 rounded-sm">
                                Empfohlen
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{row.filing}</TableCell>
                          <TableCell>{row.annual}</TableCell>
                          <TableCell>{row.tax}</TableCell>
                          <TableCell>{row.privacy}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </section>

              {/* Schritt für Schritt */}
              <section id="schritt-fuer-schritt" className="mb-16">
                <h2>Schritt-für-Schritt: LLC gründen</h2>

                <div className="space-y-6">
                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        1
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Name wählen und prüfen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Wählen Sie einen einzigartigen Namen für Ihre LLC. Der Name muss "LLC" oder 
                          "Limited Liability Company" enthalten und darf nicht mit bestehenden 
                          Unternehmen im Staat verwechselbar sein. Die meisten Gründungsservices 
                          prüfen die Verfügbarkeit automatisch.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        2
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Registered Agent bestimmen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Jede LLC benötigt einen Registered Agent mit physischer Adresse im 
                          Gründungsstaat. Für internationale Gründer ist ein professioneller 
                          Registered Agent Service wie Northwest Registered Agent die beste Wahl.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        3
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Articles of Organization einreichen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Die Articles of Organization sind das Gründungsdokument Ihrer LLC. Sie 
                          enthalten grundlegende Informationen wie Name, Registered Agent und 
                          Geschäftsadresse. Ihr Gründungsservice übernimmt die Einreichung beim Staat.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        4
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          EIN (Steuernummer) beantragen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Die Employer Identification Number (EIN) ist die Steuernummer Ihrer LLC. 
                          Sie benötigen sie für Bankkonten, Steuererklärungen und Geschäftsbeziehungen. 
                          Als Ausländer können Sie die EIN per Fax oder über Ihren Gründungsservice beantragen.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        5
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Operating Agreement erstellen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Das Operating Agreement regelt die interne Struktur und Abläufe Ihrer LLC. 
                          Auch als Single-Member LLC sollten Sie eines haben — es stärkt die 
                          Haftungstrennung. Gute Gründungsservices stellen Vorlagen bereit.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        6
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Geschäftskonto eröffnen
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Trennen Sie geschäftliche und private Finanzen strikt. Für internationale 
                          LLC-Inhaber bieten Banken wie Mercury oder Relay die Möglichkeit, Konten 
                          komplett online zu eröffnen.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="card-flat p-6">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-sm">
                        7
                      </div>
                      <div>
                        <h4 className="font-heading font-bold text-navy-900 mb-2">
                          Compliance und Jahresberichte
                        </h4>
                        <p className="text-slate-600 text-sm mb-0">
                          Halten Sie Ihre LLC in "Good Standing", indem Sie die jährlichen Berichte 
                          und Gebühren fristgerecht einreichen. Ihr Registered Agent sollte Sie an 
                          wichtige Fristen erinnern.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Kosten */}
              <section id="kosten" className="mb-16">
                <h2>Kosten einer LLC-Gründung</h2>
                <p>
                  Die Gesamtkosten für die Gründung und den laufenden Betrieb einer LLC variieren 
                  je nach Bundesstaat und gewähltem Service. Hier eine realistische Übersicht:
                </p>

                <div className="overflow-x-auto mb-8">
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Posten</TableHead>
                        <TableHead>Kosten</TableHead>
                        <TableHead>Häufigkeit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costBreakdown.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{row.item}</TableCell>
                          <TableCell>{row.cost}</TableCell>
                          <TableCell className="text-slate-600">{row.frequency}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <p>
                  <strong>Gesamtkosten im ersten Jahr (Wyoming mit Northwest):</strong> Ca. $200-$250
                  <br />
                  <strong>Laufende Kosten ab Jahr 2:</strong> Ca. $185/Jahr (Registered Agent + Annual Report)
                </p>
              </section>

              {/* Testsieger */}
              <section id="testsieger" className="mb-16">
                <h2>Unser Testsieger: Northwest Registered Agent</h2>
                
                <div className="card-highlight p-8 mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="badge-teal">Testsieger 2026</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-navy-900 mb-4">
                    Northwest Registered Agent
                  </h3>
                  <StarRating rating={4.9} size="lg" />
                  
                  <div className="mt-6 mb-6">
                    <p className="text-slate-700 mb-4">
                      Nach umfangreichen Tests von über 15 LLC-Gründungsservices empfehlen wir 
                      Northwest Registered Agent als unseren klaren Testsieger für 2026. Hier sind 
                      die Gründe:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-slate-700">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Herausragender Datenschutz:</strong> Northwest verwendet ihre eigene Adresse auf allen öffentlichen Dokumenten — Ihre persönlichen Daten bleiben privat.</span>
                      </li>
                      <li className="flex items-start gap-2 text-slate-700">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Faire Preise:</strong> $39 + Staatsgebühren für die Gründung, 1 Jahr Registered Agent kostenlos inklusive.</span>
                      </li>
                      <li className="flex items-start gap-2 text-slate-700">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>US-basierter Support:</strong> Kompetente Mitarbeiter, die tatsächlich helfen — keine Callcenter-Skripte.</span>
                      </li>
                      <li className="flex items-start gap-2 text-slate-700">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span><strong>Seit 1998 im Geschäft:</strong> Über 3 Millionen Kunden und jahrzehntelange Erfahrung.</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="https://www.awin1.com/cread.php?awinmid=66946&awinaffid=1553052&ued=https%3A%2F%2Fwww.northwestregisteredagent.com%2Fservice-signup"
                      target="_blank"
                      rel="nofollow sponsored noopener noreferrer"
                      className="cta-primary"
                      data-testid="llc-page-cta"
                    >
                      Jetzt LLC mit Northwest gründen
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                    <Link to="/northwest-registered-agent-erfahrungen" className="cta-secondary">
                      Ausführlichen Testbericht lesen
                    </Link>
                  </div>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-16">
                <h2>Häufig gestellte Fragen</h2>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left font-heading font-semibold text-navy-900">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

            </article>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="space-y-8">
              {/* TOC */}
              <TableOfContents items={tocItems} />

              {/* CTA Box */}
              <CTABox
                variant="sidebar"
                highlights={[
                  'Ab $39 + Staatsgebühren',
                  '1 Jahr Registered Agent kostenlos',
                  'Datenschutz inklusive',
                  'Operating Agreement inklusive'
                ]}
              />

              {/* Related Guides */}
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
                <h4 className="font-heading font-bold text-navy-900 mb-4">Verwandte Ratgeber</h4>
                <ul className="space-y-3">
                  <li>
                    <Link to="/ratgeber/llc-wyoming-gruenden" className="text-sm text-slate-700 hover:text-teal-600">
                      → LLC in Wyoming gründen
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/ein-nummer-beantragen" className="text-sm text-slate-700 hover:text-teal-600">
                      → EIN Nummer beantragen
                    </Link>
                  </li>
                  <li>
                    <Link to="/ratgeber/us-bankkonto-eroeffnen-auslaender" className="text-sm text-slate-700 hover:text-teal-600">
                      → US-Bankkonto als Ausländer eröffnen
                    </Link>
                  </li>
                  <li>
                    <Link to="/registered-agent" className="text-sm text-slate-700 hover:text-teal-600">
                      → Was ist ein Registered Agent?
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default LLCUSAPage;
